import { type RefObject, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const SCROLL_RESTORE_TOLERANCE_PX = 1;
export const SCROLL_RESTORE_SETTLE_MS = 500;

const scrollPositions = new Map<string, number>();

export const useScrollPositionRestoration = (
	scrollerRef: RefObject<HTMLElement | null>,
	resetKey?: string | number | boolean,
): void => {
	const { key } = useLocation();
	const storageKey = `${key}:${resetKey}`;

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;

		const savePosition = () => {
			scrollPositions.set(storageKey, scroller.scrollTop);
		};

		scroller.addEventListener("scroll", savePosition, { passive: true });
		return () => {
			scroller.removeEventListener("scroll", savePosition);
		};
	}, [storageKey, scrollerRef]);

	useLayoutEffect(() => {
		const scroller = scrollerRef.current;
		const savedPosition = scrollPositions.get(storageKey);
		if (!scroller || savedPosition === undefined) return;

		const resizeObserver = new ResizeObserver(() => restorePosition());
		let settleTimeout: ReturnType<typeof setTimeout> | undefined;

		const stopRestoring = () => {
			clearTimeout(settleTimeout);
			resizeObserver.disconnect();
			scroller.removeEventListener("touchstart", stopRestoring);
			scroller.removeEventListener("wheel", stopRestoring);
		};

		const restorePosition = () => {
			clearTimeout(settleTimeout);
			scroller.scrollTop = savedPosition;
			if (savedPosition - scroller.scrollTop <= SCROLL_RESTORE_TOLERANCE_PX) {
				stopRestoring();
				return;
			}
			settleTimeout = setTimeout(stopRestoring, SCROLL_RESTORE_SETTLE_MS);
		};

		for (const child of Array.from(scroller.children)) {
			resizeObserver.observe(child);
		}
		scroller.addEventListener("touchstart", stopRestoring, { passive: true });
		scroller.addEventListener("wheel", stopRestoring, { passive: true });
		restorePosition();

		return stopRestoring;
	}, [storageKey, scrollerRef]);
};
